<template>
  <div
    v-if="darfApiZugriffVerwalten"
    class="mt-8"
  >
    <v-divider class="mb-6"></v-divider>

    <div class="d-flex flex-wrap align-center mb-2">
      <div>
        <p class="text-title text--secondary mb-1">
          API-Zugriff für die Buchführung
        </p>
        <p class="text-caption text--secondary mb-0">
          API-Schlüssel erlauben externen Agenten einen kontrollierten Zugriff auf
          Buchführungsdaten. Der vollständige Schlüssel wird nur einmal angezeigt.
        </p>
      </div>
      <v-spacer></v-spacer>
      <v-btn
        small
        color="primary"
        class="mt-2"
        :disabled="loadingKeys"
        @click="openCreateDialog"
      >
        <v-icon left>
          mdi-key-plus
        </v-icon>
        API-Schlüssel erstellen
      </v-btn>
    </div>

    <v-alert
      v-if="message"
      dense
      text
      class="mt-4"
      :type="messageType"
    >
      {{ message }}
    </v-alert>

    <v-data-table
      class="mt-4"
      item-key="id"
      :headers="headers"
      :items="keys"
      :loading="loadingKeys"
      :items-per-page="10"
      :footer-props="footerProps"
      no-data-text="Noch keine API-Schlüssel vorhanden"
      loading-text="API-Schlüssel werden geladen ..."
    >
      <template v-slot:item.prefix="{ item }">
        <code>{{ item.prefix || '—' }}</code>
      </template>

      <template v-slot:item.scopes="{ item }">
        <div class="py-2">
          <v-chip
            v-for="scope in item.scopes"
            :key="scope"
            x-small
            outlined
            class="mr-1 mb-1"
          >
            {{ scopeLabel(scope) }}
          </v-chip>
        </div>
      </template>

      <template v-slot:item.allowedIps="{ item }">
        {{ formatAllowedIps(item.allowedIps) }}
      </template>

      <template v-slot:item.createdAt="{ item }">
        {{ formatDateTime(item.createdAt, '—') }}
      </template>

      <template v-slot:item.lastUsedAt="{ item }">
        {{ formatDateTime(item.lastUsedAt, 'Noch nie') }}
      </template>

      <template v-slot:item.expiresAt="{ item }">
        {{ formatDateTime(item.expiresAt, 'Unbegrenzt') }}
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip
          x-small
          dark
          :color="statusColor(item)"
        >
          {{ statusLabel(item) }}
        </v-chip>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-tooltip
          v-if="canRevoke(item)"
          bottom
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="error"
              aria-label="API-Schlüssel widerrufen"
              v-bind="attrs"
              v-on="on"
              @click="openRevokeDialog(item)"
            >
              <v-icon small>
                mdi-key-remove
              </v-icon>
            </v-btn>
          </template>
          <span>API-Schlüssel widerrufen</span>
        </v-tooltip>
        <span v-else>—</span>
      </template>
    </v-data-table>

    <v-dialog
      ref="createDialogComponent"
      v-model="createDialog"
      persistent
      scrollable
      max-width="720"
    >
      <v-card>
        <v-card-title id="api-create-dialog-title" class="primary white--text">
          <v-icon left dark>
            mdi-key-plus
          </v-icon>
          API-Schlüssel erstellen
        </v-card-title>

        <v-card-text class="pt-5">
          <v-form
            ref="createForm"
            v-model="createFormValid"
            @submit.prevent="createKey"
          >
            <v-text-field
              v-model="createData.name"
              label="Bezeichnung"
              hint="Zum Beispiel: Vivid-Buchführungs-Agent"
              persistent-hint
              counter="100"
              maxlength="100"
              :rules="nameRules"
            />

            <v-text-field
              v-model="createData.expiresOn"
              type="date"
              label="Gültig bis"
              :hint="`Leer: ${keyPolicy.defaultLifetimeDays} Tage Standardlaufzeit;
                maximal ${keyPolicy.maxLifetimeDays} Tage.`"
              persistent-hint
              clearable
              class="mt-3"
            />

            <v-textarea
              v-model="createData.allowedIps"
              label="Erlaubte IP-Adressen"
              hint="Optional, mehrere IP-Adressen mit Komma oder Zeilenumbruch trennen."
              persistent-hint
              rows="3"
              auto-grow
              class="mt-3"
            />

            <p class="text-subtitle-1 font-weight-medium mt-6 mb-2">
              Berechtigungen
            </p>
            <v-alert
              v-if="createData.scopes.length <= 0"
              dense
              text
              type="warning"
            >
              Bitte mindestens eine Berechtigung auswählen.
            </v-alert>

            <v-row dense>
              <v-col
                v-for="scope in scopeDefinitions"
                :key="scope.code"
                cols="12"
                md="6"
              >
                <v-card
                  outlined
                  height="100%"
                  :class="{ 'orange lighten-5': scope.destructive }"
                >
                  <v-card-text>
                    <v-checkbox
                      v-model="createData.scopes"
                      class="mt-0"
                      hide-details
                      :value="scope.code"
                      :disabled="isScopeRequired(scope.code)"
                      :color="scope.destructive ? 'warning' : 'primary'"
                    >
                      <template v-slot:label>
                        <span>
                          {{ scope.label }}
                          <v-chip
                            v-if="scope.destructive"
                            x-small
                            color="warning"
                            dark
                            class="ml-1"
                          >
                            Destruktiv
                          </v-chip>
                        </span>
                      </template>
                    </v-checkbox>
                    <p class="text-caption text--secondary ml-8 mb-0 mt-2">
                      {{ scope.description }}
                    </p>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            :disabled="creating"
            @click="closeCreateDialog"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="success"
            :loading="creating"
            :disabled="!createFormValid || createData.scopes.length <= 0"
            @click="createKey"
          >
            <v-icon left>
              mdi-key-plus
            </v-icon>
            Erstellen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      ref="tokenDialogComponent"
      v-model="tokenDialog"
      persistent
      max-width="720"
    >
      <v-card>
        <v-card-title id="api-token-dialog-title" class="success white--text">
          <v-icon left dark>
            mdi-key-variant
          </v-icon>
          API-Schlüssel wurde erstellt
        </v-card-title>
        <v-card-text class="pt-5">
          <v-alert
            dense
            text
            type="warning"
          >
            Dies ist die einzige Anzeige des vollständigen Schlüssels. Bitte jetzt sicher
            kopieren und speichern. Nach dem Schließen kann er nicht erneut angezeigt werden.
          </v-alert>
          <v-textarea
            ref="tokenField"
            class="api-token"
            label="API-Schlüssel"
            rows="3"
            readonly
            outlined
            hide-details
            :value="createdToken"
          />
          <v-alert
            v-if="copySuccessful"
            dense
            text
            type="success"
            class="mt-4 mb-0"
          >
            Der API-Schlüssel wurde in die Zwischenablage kopiert.
          </v-alert>
          <v-alert
            v-else-if="copyError"
            dense
            text
            type="error"
            class="mt-4 mb-0"
          >
            {{ copyError }}
          </v-alert>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            color="primary"
            text
            :disabled="!createdToken"
            @click="copyToken"
          >
            <v-icon left>
              mdi-content-copy
            </v-icon>
            Kopieren
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            @click="closeTokenDialog"
          >
            Ich habe den Schlüssel gespeichert
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      ref="revokeDialogComponent"
      v-model="revokeDialog"
      persistent
      max-width="520"
    >
      <v-card>
        <v-card-title id="api-revoke-dialog-title" class="error white--text">
          <v-icon left dark>
            mdi-key-remove
          </v-icon>
          API-Schlüssel widerrufen
        </v-card-title>
        <v-card-text class="pt-5">
          <template v-if="keyToRevoke">
            Soll der API-Schlüssel <b>{{ keyToRevoke.name }}</b> wirklich widerrufen werden?
            Der verbundene Agent verliert sofort den Zugriff.
          </template>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            :disabled="revoking"
            @click="closeRevokeDialog"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="error"
            :loading="revoking"
            @click="revokeKey"
          >
            Widerrufen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
const STANDARD_SCOPES = [
  'bookkeeping:read',
  'costs:write',
  'documents:write',
];

const DEFAULT_SCOPE_DEPENDENCIES = {
  'bookkeeping:read': [],
  'costs:write': ['bookkeeping:read'],
  'costs:delete': ['bookkeeping:read'],
  'documents:write': ['bookkeeping:read'],
  'evidence:write': ['bookkeeping:read'],
  'documents:delete': ['bookkeeping:read'],
  'bank:read': [],
  'bank:import': [],
  'reconciliation:write': ['bank:read', 'bookkeeping:read'],
  'provisional-income:write': ['bank:read', 'bookkeeping:read'],
};

const DEFAULT_SCOPE_DEFINITIONS = [
  {
    code: 'bookkeeping:read',
    label: 'Buchführungsdaten lesen',
    description: 'Kunden, Aufträge, Verträge, Kosten, Dokumente und Stammdaten lesen.',
    destructive: false,
  },
  {
    code: 'costs:write',
    label: 'Kosten anlegen und bearbeiten',
    description: 'Kostenpositionen erstellen und vorhandene Kostenpositionen ändern.',
    destructive: false,
  },
  {
    code: 'costs:delete',
    label: 'Kosten löschen',
    description: 'Vorhandene Kostenpositionen dauerhaft löschen.',
    destructive: true,
  },
  {
    code: 'documents:write',
    label: 'Dokumente hochladen',
    description: 'Rechnungen und Buchungsbelege an Kosten oder Aufträge anhängen.',
    destructive: false,
  },
  {
    code: 'evidence:write',
    label: 'Nachweise ausdrücklich prüfen',
    description: `Fachliche und Bankprüfungen bestätigen oder widerrufen und das
      Rechnungserfordernis mit Begründung festlegen. Erzeugt keine Datei oder Zahlung.`,
    destructive: false,
  },
  {
    code: 'documents:delete',
    label: 'Dokumente löschen',
    description: `Angehängte Rechnungen und Buchungsbelege entfernen.
      Eine geschützte Dateikopie bleibt befristet manuell wiederherstellbar.`,
    destructive: true,
  },
  {
    code: 'bank:read',
    label: 'Bankdaten lesen',
    description: 'Persistente Bankkonten, Banktransaktionen und deren Abgleichstatus lesen.',
    destructive: false,
  },
  {
    code: 'bank:import',
    label: 'Banktransaktionen importieren',
    description: `Bankkonten und Banktransaktionen providerneutral importieren oder aktualisieren,
      ohne MWE-Buchungen zu verändern.`,
    destructive: false,
  },
  {
    code: 'reconciliation:write',
    label: 'Bankabgleiche bearbeiten',
    description: `Zuordnungen zwischen Banktransaktionen und MWE-Zielen vorschlagen,
      bestätigen oder zurückweisen.`,
    destructive: false,
  },
  {
    code: 'provisional-income:write',
    label: 'Vorläufige Einnahmen bearbeiten',
    description: `Vorläufige Einnahmen aus gesicherten Bankdaten anlegen und später
      vervollständigen.`,
    destructive: false,
  },
];

function cloneScopeDependencies(dependencies) {
  return Object.keys(dependencies).reduce((result, scope) => ({
    ...result,
    [scope]: dependencies[scope].slice(),
  }), {});
}

function expandScopeDependencies(scopes, definitions, dependencies) {
  const selected = new Set(Array.isArray(scopes) ? scopes : []);
  let changed = true;
  while (changed) {
    changed = false;
    Object.keys(dependencies).forEach((scope) => {
      if (!selected.has(scope)) {
        return;
      }
      dependencies[scope].forEach((requiredScope) => {
        if (!selected.has(requiredScope)) {
          selected.add(requiredScope);
          changed = true;
        }
      });
    });
  }

  const definitionCodes = definitions.map((definition) => definition.code);
  const ordered = definitionCodes.filter((scope) => selected.has(scope));
  const additional = Array.from(selected)
    .filter((scope) => !definitionCodes.includes(scope))
    .sort();
  return ordered.concat(additional);
}

function collectRequiredScopes(scopes, dependencies) {
  const roots = Array.isArray(scopes) ? scopes : [];
  const required = new Set();
  const visit = (scope, path) => {
    const directDependencies = Array.isArray(dependencies[scope]) ? dependencies[scope] : [];
    directDependencies.forEach((requiredScope) => {
      if (path.has(requiredScope)) {
        return;
      }
      required.add(requiredScope);
      const nextPath = new Set(path);
      nextPath.add(requiredScope);
      visit(requiredScope, nextPath);
    });
  };
  roots.forEach((scope) => visit(scope, new Set([scope])));
  return required;
}

function createEmptyFormData() {
  return {
    name: '',
    scopes: STANDARD_SCOPES.slice(),
    expiresOn: '',
    allowedIps: '',
  };
}

export default {
  name: 'ApiZugriff',
  data: () => ({
    keys: [],
    scopeDefinitions: DEFAULT_SCOPE_DEFINITIONS.map((scope) => ({ ...scope })),
    scopeDependencies: cloneScopeDependencies(DEFAULT_SCOPE_DEPENDENCIES),
    keyPolicy: {
      defaultLifetimeDays: 180,
      maxLifetimeDays: 365,
    },
    loadingKeys: false,
    creating: false,
    revoking: false,
    createDialog: false,
    createFormValid: false,
    createData: createEmptyFormData(),
    tokenDialog: false,
    createdToken: '',
    copySuccessful: false,
    copyError: '',
    revokeDialog: false,
    keyToRevoke: null,
    message: '',
    messageType: 'info',
    nameRules: [
      (value) => Boolean(value && String(value).trim()) || 'Bitte eine Bezeichnung angeben.',
    ],
    footerProps: {
      'items-per-page-options': [10, 25, 50],
      'items-per-page-text': 'Einträge pro Seite',
    },
  }),
  computed: {
    darfApiZugriffVerwalten() {
      const { save } = this.$store.state.main;
      return Boolean(
        save.user
        && Array.isArray(save.user.rechte)
        && save.user.rechte.includes('x|410|set')
        && save.user.rechte.includes('s|410|api'),
      );
    },
    headers() {
      return [
        { text: 'Bezeichnung', value: 'name' },
        { text: 'Schlüssel', value: 'prefix', sortable: false },
        { text: 'Berechtigungen', value: 'scopes', sortable: false },
        { text: 'IP-Beschränkung', value: 'allowedIps', sortable: false },
        { text: 'Erstellt', value: 'createdAt' },
        { text: 'Zuletzt genutzt', value: 'lastUsedAt' },
        { text: 'Gültig bis', value: 'expiresAt' },
        { text: 'Status', value: 'status' },
        { text: 'Aktionen', value: 'actions', sortable: false, align: 'center' },
      ];
    },
  },
  watch: {
    createDialog(open) {
      if (open) this.labelDialogContent('createDialogComponent', 'api-create-dialog-title');
    },
    tokenDialog(open) {
      if (open) this.labelDialogContent('tokenDialogComponent', 'api-token-dialog-title');
    },
    revokeDialog(open) {
      if (open) this.labelDialogContent('revokeDialogComponent', 'api-revoke-dialog-title');
    },
    'createData.scopes': {
      handler(scopes) {
        const expandedScopes = expandScopeDependencies(
          scopes,
          this.scopeDefinitions,
          this.scopeDependencies,
        );
        if (!Array.isArray(scopes) || scopes.length !== expandedScopes.length
          || scopes.some((scope, index) => scope !== expandedScopes[index])) {
          this.createData.scopes = expandedScopes;
        }
      },
      deep: true,
    },
  },
  methods: {
    labelDialogContent(refName, titleId) {
      this.$nextTick(() => {
        const dialogComponent = this.$refs[refName];
        const content = dialogComponent && dialogComponent.$refs
          ? dialogComponent.$refs.content : null;
        if (content) content.setAttribute('aria-labelledby', titleId);
      });
    },
    isScopeRequired(scopeCode) {
      const otherScopes = this.createData.scopes.filter((scope) => scope !== scopeCode);
      return collectRequiredScopes(otherScopes, this.scopeDependencies).has(scopeCode);
    },
    async sendRequest(action, fields = {}) {
      const formData = new FormData();
      formData.append('code', this.$store.state.login.session.code);
      formData.append('action', action);
      Object.keys(fields).forEach((field) => {
        formData.append(field, fields[field]);
      });

      let response;
      try {
        response = await fetch(
          `${process.env.VUE_APP_API_HOST}/integrations/bookkeeping/manage.php`,
          {
            method: 'POST',
            body: formData,
          },
        );
      } catch {
        throw new Error('Der Server konnte nicht erreicht werden.');
      }

      let result;
      try {
        result = await response.json();
      } catch {
        throw new Error('Die Serverantwort konnte nicht verarbeitet werden.');
      }

      if (result.status === 'accessError') {
        this.$store.commit('login/backendErrorSession');
        throw new Error(result.message || 'Der Zugriff auf die API-Verwaltung wurde verweigert.');
      }
      if (!response.ok || result.status !== 'success') {
        throw new Error(result.message || 'Die API-Verwaltung konnte nicht ausgeführt werden.');
      }
      return result;
    },
    normalizeScopes(scopes) {
      if (Array.isArray(scopes)) {
        return scopes;
      }
      if (typeof scopes !== 'string' || scopes === '') {
        return [];
      }
      try {
        const parsedScopes = JSON.parse(scopes);
        return Array.isArray(parsedScopes) ? parsedScopes : [];
      } catch {
        return scopes.split(',').map((scope) => scope.trim()).filter(Boolean);
      }
    },
    applyResponse(result) {
      if (Array.isArray(result.keys)) {
        this.keys = result.keys.map((key) => ({
          ...key,
          scopes: this.normalizeScopes(key.scopes),
        }));
      }
      if (Array.isArray(result.scopeDefinitions) && result.scopeDefinitions.length > 0) {
        this.scopeDefinitions = result.scopeDefinitions.map((scope) => ({
          ...scope,
          destructive: Boolean(scope.destructive),
        }));
      }
      if (result.scopeDependencies && typeof result.scopeDependencies === 'object'
        && !Array.isArray(result.scopeDependencies)) {
        const knownScopes = this.scopeDefinitions.map((scope) => scope.code);
        this.scopeDependencies = knownScopes.reduce((dependencies, scope) => ({
          ...dependencies,
          [scope]: Array.isArray(result.scopeDependencies[scope])
            ? result.scopeDependencies[scope]
              .filter((requiredScope) => knownScopes.includes(requiredScope))
            : [],
        }), {});
        this.createData.scopes = expandScopeDependencies(
          this.createData.scopes,
          this.scopeDefinitions,
          this.scopeDependencies,
        );
      }
      if (result.keyPolicy && typeof result.keyPolicy === 'object') {
        const defaultLifetimeDays = Number(result.keyPolicy.defaultLifetimeDays);
        const maxLifetimeDays = Number(result.keyPolicy.maxLifetimeDays);
        if (Number.isInteger(defaultLifetimeDays) && defaultLifetimeDays > 0
          && Number.isInteger(maxLifetimeDays) && maxLifetimeDays >= defaultLifetimeDays) {
          this.keyPolicy = {
            defaultLifetimeDays,
            maxLifetimeDays,
          };
        }
      }
    },
    setMessage(type, message) {
      this.messageType = type;
      this.message = message;
    },
    async loadKeys() {
      this.loadingKeys = true;
      this.message = '';
      try {
        const result = await this.sendRequest('list');
        this.applyResponse(result);
      } catch (error) {
        this.setMessage(
          'error',
          error.message || 'Die API-Schlüssel konnten nicht geladen werden.',
        );
      } finally {
        this.loadingKeys = false;
      }
    },
    resetCreateForm() {
      this.createData = createEmptyFormData();
      this.createFormValid = false;
      this.$nextTick(() => {
        if (this.$refs.createForm) {
          this.$refs.createForm.resetValidation();
        }
      });
    },
    openCreateDialog() {
      this.message = '';
      this.resetCreateForm();
      this.createDialog = true;
    },
    closeCreateDialog() {
      if (!this.creating) {
        this.createDialog = false;
        this.resetCreateForm();
      }
    },
    async createKey() {
      if (!this.$refs.createForm || !this.$refs.createForm.validate()) {
        return;
      }
      if (this.createData.scopes.length <= 0) {
        return;
      }

      this.creating = true;
      this.message = '';
      try {
        const result = await this.sendRequest('create', {
          name: this.createData.name.trim(),
          scopes: JSON.stringify(this.createData.scopes),
          expiresOn: this.createData.expiresOn || '',
          allowedIps: this.createData.allowedIps.trim(),
        });
        this.applyResponse(result);
        this.createDialog = false;
        this.resetCreateForm();

        if (typeof result.token !== 'string' || result.token === '') {
          this.setMessage(
            'error',
            `Der API-Schlüssel wurde erstellt, konnte aber nicht angezeigt werden.
            Bitte widerrufen und einen neuen Schlüssel erstellen.`,
          );
          return;
        }

        this.createdToken = result.token;
        this.copySuccessful = false;
        this.copyError = '';
        this.tokenDialog = true;
      } catch (error) {
        this.setMessage(
          'error',
          error.message || 'Der API-Schlüssel konnte nicht erstellt werden.',
        );
      } finally {
        this.creating = false;
      }
    },
    copyTokenFallback() {
      const textArea = document.createElement('textarea');
      textArea.value = this.createdToken;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        return document.execCommand('copy');
      } finally {
        document.body.removeChild(textArea);
      }
    },
    async copyToken() {
      if (!this.createdToken) {
        return;
      }

      let copied = false;
      try {
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
          await navigator.clipboard.writeText(this.createdToken);
          copied = true;
        } else {
          copied = this.copyTokenFallback();
        }
      } catch {
        try {
          copied = this.copyTokenFallback();
        } catch {
          copied = false;
        }
      }

      this.copySuccessful = copied;
      if (!copied) {
        this.copyError = `Der API-Schlüssel konnte nicht automatisch kopiert werden.
          Bitte manuell markieren.`;
      } else {
        this.copyError = '';
      }
    },
    closeTokenDialog() {
      this.tokenDialog = false;
      this.createdToken = '';
      this.copySuccessful = false;
      this.copyError = '';
    },
    openRevokeDialog(key) {
      this.keyToRevoke = key;
      this.revokeDialog = true;
      this.message = '';
    },
    closeRevokeDialog() {
      if (!this.revoking) {
        this.revokeDialog = false;
        this.keyToRevoke = null;
      }
    },
    async revokeKey() {
      if (!this.keyToRevoke) {
        return;
      }

      this.revoking = true;
      try {
        const result = await this.sendRequest('revoke', {
          id: this.keyToRevoke.id,
        });
        const revokedKeyName = this.keyToRevoke.name;
        this.applyResponse(result);
        this.revokeDialog = false;
        this.keyToRevoke = null;
        this.setMessage('success', `Der API-Schlüssel „${revokedKeyName}“ wurde widerrufen.`);
      } catch (error) {
        this.setMessage(
          'error',
          error.message || 'Der API-Schlüssel konnte nicht widerrufen werden.',
        );
      } finally {
        this.revoking = false;
      }
    },
    scopeLabel(scopeCode) {
      const scope = this.scopeDefinitions.find((definition) => definition.code === scopeCode);
      return scope ? scope.label : scopeCode;
    },
    formatAllowedIps(allowedIps) {
      if (Array.isArray(allowedIps)) {
        return allowedIps.length > 0 ? allowedIps.join(', ') : 'Alle IP-Adressen';
      }
      return allowedIps || 'Alle IP-Adressen';
    },
    formatDateTime(value, emptyText) {
      if (!value) {
        return emptyText;
      }
      let normalizedValue = value;
      if (typeof normalizedValue === 'string') {
        normalizedValue = normalizedValue.replace(
          /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})$/,
          '$1T$2',
        );
      }
      const date = new Date(normalizedValue);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return date.toLocaleString('de-DE', {
        dateStyle: 'short',
        timeStyle: 'short',
      });
    },
    normalizedStatus(key) {
      if (key.revokedAt) {
        return 'revoked';
      }
      return String(key.status || 'active').toLowerCase();
    },
    statusLabel(key) {
      const labels = {
        active: 'Aktiv',
        revoked: 'Widerrufen',
        expired: 'Abgelaufen',
        owner_inactive: 'Ersteller nicht berechtigt',
      };
      const status = this.normalizedStatus(key);
      return labels[status] || key.status || 'Unbekannt';
    },
    statusColor(key) {
      const colors = {
        active: 'success',
        revoked: 'error',
        expired: 'grey',
        owner_inactive: 'warning',
      };
      return colors[this.normalizedStatus(key)] || 'grey';
    },
    canRevoke(key) {
      return this.normalizedStatus(key) !== 'revoked';
    },
  },
  created() {
    if (this.darfApiZugriffVerwalten) {
      this.loadKeys();
    }
  },
  beforeDestroy() {
    this.createdToken = '';
  },
};
</script>

<style scoped>
.api-token {
  font-family: monospace;
}
</style>
